import React from 'react';
import { FileSpreadsheet, FileText, FileWarning, X } from 'lucide-react';

/**
 * One file row of an upload list, ported from `design/RFQ Wizard v2.dc.html`
 * (wizard Step 1; the datasheet library page reuses it). Presentational only;
 * states are per row, so the list is already shaped for the upcoming true
 * multi-file (up to 10) + cross-referencing capability. `ready` = selected
 * locally, not sent yet (the datasheet page batches before uploading).
 */

export type UploadRowStatus = 'ready' | 'uploading' | 'uploaded' | 'extracted' | 'failed';

export interface UploadRow {
  name: string;
  size: number;
  status: UploadRowStatus;
  /** 0–100 while uploading. */
  progress?: number;
  /** Extracted BOM row only — shown as "N components found". */
  componentsFound?: number;
  /** Failure detail (failed rows). */
  error?: string;
}

/** "148 KB" / "2.1 MB" — matches the mock's mono meta line. */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const metaText = (row: UploadRow): string => {
  const size = formatFileSize(row.size);
  switch (row.status) {
    case 'ready':
      return `${size} · ready to upload`;
    case 'extracted':
      return `${size} · ${row.componentsFound ?? 0} components found`;
    case 'uploaded':
      // The status pill already says "Uploaded"; the meta line keeps to the size.
      return size;
    case 'uploading':
      return `${size} · uploading…`;
    case 'failed':
      return `${size} · ${row.error || 'upload failed'}`;
  }
};

const IconTile: React.FC<{ status: UploadRowStatus }> = ({ status }) => {
  if (status === 'extracted') {
    return (
      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-control border border-success-border bg-success-bg">
        <FileSpreadsheet className="h-4 w-4 text-success-ink" />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-control border border-danger-border bg-danger-bg">
        <FileWarning className="h-4 w-4 text-danger-ink" />
      </span>
    );
  }
  return (
    <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-control border border-[#E4E8EF] bg-canvas">
      <FileText className="h-4 w-4 text-ink-secondary" />
    </span>
  );
};

const pillBase =
  'whitespace-nowrap rounded-pill border px-[9px] py-[3px] text-meta font-semibold';

const StatusSide: React.FC<{ row: UploadRow }> = ({ row }) => {
  switch (row.status) {
    case 'ready':
      return <span className={`${pillBase} border-line bg-canvas text-ink-secondary`}>Ready</span>;
    case 'extracted':
      return <span className={`${pillBase} border-success-border bg-success-bg text-success-ink`}>Extracted</span>;
    case 'uploaded':
      return <span className={`${pillBase} border-[#D6E4FF] bg-[#F0F6FF] text-[#175CD3]`}>Uploaded</span>;
    case 'failed':
      return (
        <span className={`${pillBase} border-danger-border bg-danger-bg text-danger-ink`} title={row.error}>
          Failed
        </span>
      );
    case 'uploading': {
      const pct = Math.max(0, Math.min(100, row.progress ?? 0));
      return (
        <div className="flex shrink-0 items-center gap-s3">
          <div className="h-[4px] w-[90px] overflow-hidden rounded-round bg-line-divider-soft">
            <div className="h-full bg-pipeline-4 transition-[width]" style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-[11px] text-ink-secondary">{pct}%</span>
        </div>
      );
    }
  }
};

export interface UploadFileRowProps {
  row: UploadRow;
  onRemove?: () => void;
}

export const UploadFileRow: React.FC<UploadFileRowProps> = ({ row, onRemove }) => (
  <div className="flex items-center gap-[12px] border-b border-line-divider px-[18px] py-[12px]">
    <IconTile status={row.status} />
    <div className="min-w-0 flex-1">
      <div className="truncate text-row-title text-ink">{row.name}</div>
      <div className="mt-[2px] font-mono text-[11px] text-ink-muted">{metaText(row)}</div>
    </div>
    <StatusSide row={row} />
    {onRemove && (
      <button
        type="button"
        aria-label={`Remove ${row.name}`}
        onClick={onRemove}
        className="shrink-0 text-ink-muted transition-colors hover:text-danger-ink"
      >
        <X className="h-[14px] w-[14px]" />
      </button>
    )}
  </div>
);

export default UploadFileRow;
